import React, { Fragment, useState } from "react";
import { Project } from "./getProjectsService";
import { Listbox, Transition } from "@headlessui/react";
import { ChevronDownIcon, XCircleIcon } from "@heroicons/react/24/solid";

interface ProjectSelectorProps {
  selectedBrand: string | undefined;
  selectedBrands: Project[];
  projectsData: Project[] | undefined;
  onChangeProject: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onClearProjects: () => void;
}

export default function ProjectSelector({
  selectedBrand,
  selectedBrands,
  projectsData,
  onChangeProject,
  onClearProjects,
}: ProjectSelectorProps) {
  const [query, setQuery] = useState("");

  const selectedProject = projectsData?.find(
    (p) => String(p.code) === selectedBrand
  );

  const filteredProjects =
    query === ""
      ? projectsData
      : projectsData?.filter((project) => {
          const combined = `${project.code} ${project.name}`.toLowerCase();
          return combined.includes(query.toLowerCase());
        });

  return (
    <div>
      <div className="flex items-center gap-4">
        <div className="relative w-full max-w-md flex-grow">
          <Listbox
            value={selectedProject}
            onChange={(project: Project | undefined) => {
              const event = {
                target: { value: project?.code ?? "" },
              } as React.ChangeEvent<HTMLSelectElement>;
              onChangeProject(event);
              setQuery(""); // Clear search after selection
            }}
          >
            {({ open }) => (
              <>
                <div className="relative">
                  <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm h-[42px]">
                    <span className="block whitespace-normal">
                      {selectedProject
                        ? `${selectedProject.code} - ${selectedProject.name}`
                        : selectedBrands?.length > 0
                        ? "Select a project"
                        : "All projects"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDownIcon
                        className={`h-5 w-5 text-gray-400 transition-transform ${
                          open ? "rotate-180" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </span>
                  </Listbox.Button>

                  <Transition
                    as="div"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    {open && (
                      <div className="absolute z-10 mt-1 min-w-full w-auto max-w-2xl rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <div className="relative">
                          <Listbox.Options
                            className={`${
                              filteredProjects && filteredProjects.length > 5
                                ? "max-h-96 overflow-y-scroll"
                                : "max-h-auto"
                            } py-1`}
                          >
                            {filteredProjects && filteredProjects.length > 0 ? (
                              filteredProjects.map((project) => (
                                <Listbox.Option
                                  key={String(project.code)}
                                  className={({ active }) =>
                                    `relative cursor-default select-none py-3 pl-10 pr-4 ${
                                      active
                                        ? "bg-blue-300 text-black"
                                        : "bg-gray-50 text-gray-900"
                                    } hover:bg-blue-200`
                                  }
                                  value={project}
                                >
                                  {({ selected }) => (
                                    <span
                                      className={`block truncate ${
                                        selected ? "font-medium" : "font-normal"
                                      }`}
                                    >
                                      {`${project.code} - ${project.name}`}
                                    </span>
                                  )}
                                </Listbox.Option>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-gray-500">
                                No results found.
                              </div>
                            )}
                          </Listbox.Options>
                          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent" />
                        </div>
                      </div>
                    )}
                  </Transition>
                </div>
              </>
            )}
          </Listbox>
        </div>

        {/* Clear Button */}
        <button
          type="button"
          className="h-[42px] px-4 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
          onClick={onClearProjects}
          id="btnClearProjects"
          disabled={!selectedBrands || selectedBrands.length === 0}
        >
          <XCircleIcon className="h-5 w-5" aria-hidden="true" />
          <span>Clear</span>
        </button>
      </div>
    </div>
  );
}
